import {noop} from '@benchling/js-utils';
import {StyleSheet} from 'aphrodite';
import {NodeProps} from 'reactflow';

import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {LookupConfigSpec} from 'lookup-config/LookupConfigModalConstants';
import {ProcessFlowchartValidationError} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {MIN_ZOOM, MAX_ZOOM} from 'ui-library/reactflow/controls/HorizontalControls';
import {EdgeType, ElkEdgeData} from 'ui-library/reactflow/edges/edgeOptions';
import {REACT_FLOW_PRO_OPTIONS} from 'ui-library/reactflow/ReactFlowConstants';

import {ProcessFlowchartConfigEditorQuery} from './__generated/ProcessFlowchartConfigEditorQuery';
import {
  TaskSchemaNodeData,
  TaskSchemaInteractionNodeData,
  StaticFlowchartNodeData,
  FlowchartNode,
  TaskDefinitionVersionSpecWithTaskSchema,
  TaskDefinitionUpdater,
} from './processFlowchartConfigEditorNodeTypes';
import {FlowchartConfigAutosaverPausedConditionType} from './redux/reduceFlowchartConfig';

export type ProcessFlowchartConfigGQL = ExtractByTypename<
  ProcessFlowchartConfigEditorQuery['processFlowchartConfig'],
  'ProcessFlowchartConfigNode'
>;
export type ProcessFlowchartConfigWithValidSchemaGQL = ProcessFlowchartConfigGQL & {
  schema: ExtractByTypename<ProcessFlowchartConfigGQL['schema'], 'ProcessTaskSchemaNode'>;
  taskDefinition: ExtractByTypename<
    ProcessFlowchartConfigGQL['taskDefinition'],
    'ProcessTaskDefinitionNode'
  > & {
    latestVersion: ExtractByTypename<
      ExtractByTypename<
        ProcessFlowchartConfigGQL['taskDefinition'],
        'ProcessTaskDefinitionNode'
      >['latestVersion'],
      'ProcessTaskDefinitionVersionNode'
    >;
  };
};

export function isFlowchartConfigWithValidSchemasAndTaskDefinition(
  flowchartConfig: ProcessFlowchartConfigGQL
): flowchartConfig is ProcessFlowchartConfigWithValidSchemaGQL {
  return (
    !isInaccessible(flowchartConfig.schema) &&
    !isInaccessible(flowchartConfig.taskDefinition) &&
    flowchartConfig.taskDefinition.latestVersion != null
  );
}

export type ProcessFlowchartConfigVersionGQL = ExtractByTypename<
  ProcessFlowchartConfigGQL['flowchartConfigVersion'],
  'ProcessFlowchartConfigVersionNode'
>;
export type ProcessFlowchartNodeGraphQL = ExtractByTypename<
  NonNullable<ProcessFlowchartConfigVersionGQL['templateFlowchart']>['nodeConfigs'][number],
  'ProcessFlowchartNodeConfigNode'
>;
export type InboundSchemaMapperGQL = ProcessFlowchartNodeGraphQL['inboundSchemaMappers'][number];
export type SchemaMapperDependencyGQL = ExtractByTypename<
  InboundSchemaMapperGQL['schemaMapperDependencies'][number],
  'ProcessFlowchartSchemaMapperDependencyNode'
>;
export type FieldMapperConfigGQL = ExtractByTypename<
  SchemaMapperDependencyGQL['fieldMapperConfigs'][number],
  'ProcessFlowchartSchemaFieldMapperConfigNode'
>;
export type ProcessTaskDefinitionVersionGraphQL = ExtractByTypename<
  ExtractByTypename<
    NonNullable<ProcessFlowchartNodeGraphQL['nodeDetails']>,
    'ProcessTaskNodeDetailsNode'
  >['processTaskDefinitionVersion'],
  'ProcessTaskDefinitionVersionNode'
>;
export type FieldDefinitionGraphQL = ExtractByTypename<
  ProcessTaskDefinitionVersionGraphQL['inputFieldDefinitions'][number],
  'ProcessFieldDefinitionNode'
>;
export interface NodeDetailsSidepanelContentBaseProps<TApplicationData = unknown> {
  flowchartConfigId: string;
  onChangeSelectedFieldId: (fieldId: string | null) => void;
  flowchart: TaskDefinitionVersionSpecWithTaskSchemaFlowchart;
  locked?: boolean;
  onUpdateTaskDefinitionVersion: TaskDefinitionUpdater<TApplicationData>;
  validationErrors: Array<ProcessFlowchartValidationError>;
}

export interface NodeDetailsSidepanelReduxProps {
  isAutosaverPaused: boolean;
  setIsAutosaverPaused: (
    condition: FlowchartConfigAutosaverPausedConditionType,
    conditionValue: boolean
  ) => void;
  isAutosaverBlocked: boolean;
  isSaveInFlight: boolean;
  isDirty: boolean;
  isReadonly: boolean;
}

export type NodeDetailsSidepanelContentProps<TApplicationData = unknown> =
  NodeDetailsSidepanelContentBaseProps<TApplicationData> & NodeDetailsSidepanelReduxProps;

export interface TaskNodeDetailsSidepanelContentProps<
  TApplicationData = unknown,
> extends NodeDetailsSidepanelContentProps<TApplicationData> {
  selectedNodeTaskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema<TApplicationData>;
  selectedNodeId: string;
  selectedFieldId: string | null;
}

export enum NodeSettingsTabKeys {
  FLOWCHART_SETTINGS = 'FLOWCHART_SETTINGS',
  INPUT_OUTPUT_FIELDS = 'INPUT_OUTPUT_FIELDS',
  FIELD_MAPPING_INFO = 'FIELD_MAPPING_INFO',
  TASK_SCHEMA_DETAILS = 'TASK_SCHEMA_DETAILS',
}
export interface SidepanelTab {
  key: NodeSettingsTabKeys;
  label: string;
  content: React.ComponentType<TaskNodeDetailsSidepanelContentProps>;
  openOnFieldSelect?: boolean;
}
/**
 * Grid spacing in pixels for the flowchart editor.
 * This is used to snap the nodes to a grid when dragging them around.
 * Larger numbers = further distance between snap points
 */
export const SNAP_TO_GRID_PIXEL_SPACING = 10;
/**
 * Horizontal grid spacing (px), vertical grid spacing (px)
 */
export const SNAP_GRID: [number, number] = [SNAP_TO_GRID_PIXEL_SPACING, SNAP_TO_GRID_PIXEL_SPACING];

export const DEFAULT_FLOWCHART_PROPS = {
  proOptions: REACT_FLOW_PRO_OPTIONS,
  panOnScroll: true,
  panOnDrag: true,
  zoomOnScroll: true,
  zoomOnPinch: true,
  zoomActivationKeyCode: ['Alt', 'Meta'],
  // This overrides any default viewport settings, initializes canvas with all nodes in view
  // Within the bounds of the min/max zoom levels
  fitView: true,
  snapToGrid: true,
  snapGrid: SNAP_GRID,
  minZoom: MIN_ZOOM,
  maxZoom: MAX_ZOOM,
};

export const READ_ONLY_FLOWCHART_PROPS = {
  nodesDraggable: false,
  nodesConnectable: false,
  elementsSelectable: true,
  onConnect: noop,
};

export const EDITABLE_FLOWCHART_PROPS = {
  nodesDraggable: true,
  nodesConnectable: true,
  selectNodesOnDrag: false,
  elementsSelectable: true,
};

export const STANDARD_ROOT_NODE_NAME = 'Start';
export const STANDARD_ROOT_NODE_ICON: BenchlingIconName = 'start-node';
export const STANDARD_END_NODE_NAME = 'End';
export const STANDARD_END_NODE_ICON: BenchlingIconName = 'end-node';
export const TASK_SCHEMA_NODE_ICON = 'process_task_schema';

export enum ProcessFlowchartNodeType {
  TASK_SCHEMA_NODE = 'TaskSchemaNode',
  ROUTER_NODE = 'RouterNode',
  STOP_NODE = 'StopNode',
  /**
   * A placeholder node represents a node in the process of being created.
   */
  PLACEHOLDER_NODE = 'PlaceholderNode',
}

export type UserFacingNodeTypes = Exclude<
  ProcessFlowchartNodeType,
  ProcessFlowchartNodeType.PLACEHOLDER_NODE
>;

export enum ProcessFlowchartEdgeType {
  CONTROL_FLOW = 'control-flow',
  DATA_FLOW = 'data-flow',
}

export interface LogicNodeStaticData {
  id: string;
  type: ProcessFlowchartNodeType;
  sections: Array<unknown>;
}

export interface FlowchartStaticEdgeData {
  id: string;
  source: string;
  target: string;
  sourceHandle: string;
  targetHandle: string;
  type: ProcessFlowchartEdgeType;
  label?: string;
}

export interface FlowchartEdge extends FlowchartStaticEdgeData {
  data?: {
    validationErrors?: Array<ProcessFlowchartValidationError> | null;
    elkEdgeData?: ElkEdgeData;
    type: EdgeType.ELK_SPLINE;
  };
  selected?: boolean;
}

export enum TaskSchemaNodeSectionType {
  MAP_FROM_START = 'Map from Start',
  INPUTS = 'Inputs',
  OUTPUTS = 'Outputs',
  NOT_MAPPABLE = 'Not Mappable',
}

export enum TaskSchemaNodeSection {
  MAP_FROM_START = 'Map from Start',
  INPUTS = 'Inputs',
  OUTPUTS = 'Outputs',
}

export enum ProcessFlowchartNodeSection {
  TASK_SCHEMAS = 'TASK_SCHEMAS',
  LOGIC = 'LOGIC',
  PIPELINES = 'PIPELINES',
}

/**
 * The source of the transition.
 *
 * This is used to determine which objects will be used as the input to the mapper at execution time.
 * In the backend, this defines which object (the task or output) is used as the "token" that is processed by
 * the workflow orchestration system.
 *
 * - If the source is "inputs", then we create a single downstream element for every input to the source node.
 *   In other words, the "process task" is used as the token in workflow orchestration.
 * - If the source is "outputs", then we create a single downstream element for every outupt of the source node.
 *   In other words, the "process output" is used as the token in workflow orchestration.
 */
export enum TransitionSource {
  OUTPUTS = 'outputs',
  INPUTS = 'inputs',
}

export interface CustomMappingData {
  lookupConfig?: LookupConfigSpec;
  resultMustBeNonNullToMap?: boolean;
}

export function isCustomMappingDataEmpty(customMappingData: CustomMappingData): boolean {
  return customMappingData.lookupConfig == null && !customMappingData.resultMustBeNonNullToMap;
}

export const FLOWCHART_TASK_SECONDARY_INFO = 'Flowchart task';
export const FLOWCHART_OUTPUT_SECONDARY_INFO = 'Flowchart output';

export const PROCESS_FLOWCHART_CONFIG_EDITOR_STYLES = StyleSheet.create({
  // TODO: Remove this once transition modal constants are consolidated with this file
  editorBodyWrapper: {
    overflowY: 'hidden',
  },
  flowchartEditorContentWrapper: {
    display: 'flex',
    /**
     * ReactFlow needs its immediate container to have an explicit height.
     * The controller ensures the parent container uses flex layout to consume
     * the remaining viewport height, so this wrapper can simply stretch to 100%.
     */
    height: '100%',
    width: '100%',
  },
  flowchartEditorContent: {
    display: 'flex',
    height: '100%',
    position: 'relative',
  },
});

// Allows for filtering out interaction fields from static node data
// When a new field is added to TaskSchemaInteractionNodeData, will get compilation error here
type InteractionKeys = keyof TaskSchemaInteractionNodeData;
const interactionFields: Record<InteractionKeys, undefined> = {
  onNodeHeaderClick: undefined,
  onNodeDelete: undefined,
  nodeDetailsSidepanelContent: undefined,
  onFieldClick: undefined,
  onUpdateNodeData: undefined,
  getAddNodeOptionsForRightHandle: undefined,
  validationErrors: undefined,
};
export const INTERACTION_FIELDS = Object.keys(interactionFields);

export interface StaticTemplateFlowchart {
  nodesStaticData: Array<StaticFlowchartNodeData>;
  edgesStaticData: Array<FlowchartStaticEdgeData>;
}

export interface InteractiveTemplateFlowchart {
  nodes: Array<FlowchartNode>;
  edges: Array<FlowchartEdge>;
}

export interface TaskDefinitionVersionSpecEdge {
  fromNodeClientId: string;
  fromFieldClientId?: string;
  toNodeClientId: string;
  toFieldClientId?: string;
  type: ProcessFlowchartEdgeType;
}

export interface TaskDefinitionVersionSpecWithTaskSchemaFlowchart<TApplicationData = unknown> {
  // Mapping of nodeId -> TaskDefinitionVersionSpecWithTaskSchema
  nodes: Record<string, TaskDefinitionVersionSpecWithTaskSchema<TApplicationData>>;
  edges: Array<TaskDefinitionVersionSpecEdge>;
}

export const NODE_SECTION_TO_HEADER_TITLE: Record<ProcessFlowchartNodeSection, string> = {
  [ProcessFlowchartNodeSection.TASK_SCHEMAS]: 'Task schemas',
  [ProcessFlowchartNodeSection.LOGIC]: 'Logic',
  [ProcessFlowchartNodeSection.PIPELINES]: 'Pipelines',
};
/**
 * Props for the task schema node renderer
 */

export type TaskSchemaNodeRendererProps = NodeProps<TaskSchemaNodeData>;

export interface SchemaMapperConfig {
  clientId: string;
  fromTaskDefinitionVersionIds: Array<string>;
  toTaskDefinitionVersionId: string;
}
